<template>
  <p-drawer :steps="supportDetailSteps()" @on-close="smartRouter.back()">
    <template #1>
      <div v-if="isLoading" class="d-flex justify-content-center align-items-center py-32">
        <p-loading size="lg" />
      </div>
      <div v-else-if="ticket">
        <div class="d-flex flex-column">
          <div class="d-flex justify-content-between flex-row py-8">
            <div class="f-utility-14 t-fg-tertiary">{{ $t('supportDetail.supportCode') }}</div>
            <div class="f-utility-14 t-fg-primary" v-text="ticket?.id" />
          </div>
          <p-divider />
          <div class="d-flex justify-content-between flex-row py-8">
            <div class="f-utility-14 t-fg-tertiary">{{ $t('supportDetail.solutionTime') }}</div>
            <div class="f-utility-14 t-fg-primary" v-text="solutionTime" />
          </div>
        </div>
        <div class="mt-32">
          <p class="f-body-16 t-fg-primary mb-8">{{ $t('supportDetail.requestProcess') }}</p>
          <div class="p-page-subtitle__divider mb-24" />
          <p-timeline :items="timelineItems" />
        </div>
        <div v-if="isShowFeedbackForm" class="mt-24">
          <p-button size="lg" block="false" class="full-width" @click="isShowModal = true">
            {{ $t('supportDetail.evaluate') }}
            <p-icon icon="star-ratings" size="xmd" color="fix-white" />
          </p-button>
        </div>
      </div>
      <p-modal :modal-visibility="isShowModal" width="384px" @on-close="isShowModal = false">
        <p-feedback-form
          :choices-by-rating="feedbackStore.currentFeedback?.choices_by_rating || {}"
          :loading="feedbackStore.loading"
          :error="feedbackStore.error"
          @rate="onRate"
          @submit="onFeedbackSubmit"
        />
      </p-modal>
    </template>
  </p-drawer>
</template>

<script lang="ts" setup>
import { useFeedbackStore } from '@/store/feedback'

const ticketStore = useTicketStore()
const feedbackStore = useFeedbackStore()
const route = useRoute()
const { t } = useI18n()
const isShowFeedbackForm = ref(false)
const isShowModal = ref(false)
const isLoading = ref(true)

const ticket = computed(() => ticketStore.currentTicket)

const ratingValue = ref(0)

function onRate(stars: number) {
  ratingValue.value = stars
  // No persistence on rate-pick — preserves the existing behavior of
  // submitting only when the user clicks the form's submit button.
}

async function onFeedbackSubmit(choiceIds: number[], comment: string) {
  feedbackStore.clearError()
  await feedbackStore.submitFeedback(Number(route.query.id), {
    rating: ratingValue.value,
    choice_ids: choiceIds.length > 0 ? choiceIds : undefined,
    comment: comment || undefined,
  })
}

const solutionTime = computed(() => {
  if (!ticket.value?.created_at || !ticket.value?.last_message_at) return '-'

  const createdAt = new Date(ticket.value.created_at)
  const lastMessageAt = new Date(ticket.value.last_message_at)

  const diffMs = lastMessageAt.getTime() - createdAt.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays > 0) {
    const remainingHours = diffHours % 24
    return remainingHours > 0
      ? `${diffDays} ${t('supportDetail.timeUnit.day')} ${remainingHours} ${t('supportDetail.timeUnit.hour')}`
      : `${diffDays} ${t('supportDetail.timeUnit.day')}`
  } else if (diffHours > 0) {
    const remainingMinutes = diffMinutes % 60
    return remainingMinutes > 0
      ? `${diffHours} ${t('supportDetail.timeUnit.hour')} ${remainingMinutes} ${t('supportDetail.timeUnit.minute')}`
      : `${diffHours} ${t('supportDetail.timeUnit.hour')}`
  } else {
    return `${diffMinutes} ${t('supportDetail.timeUnit.minute')}`
  }
})

const supportDetailSteps = () => ({
  1: {
    title: ticket.value?.subject || '',
    back: true,
    url: '1',
  },
})

const formatStatusDescription = (statusId: number): string => {
  // Each backend status maps to its own label (see ticket.status.*):
  //   2 active, 3 waiting, 4 resolved, 5 closed.
  const statusMap: Record<number, string> = {
    2: 'ticket.status.active',
    3: 'ticket.status.waiting',
    4: 'ticket.status.resolved',
    5: 'ticket.status.closed',
  }

  const statusKey = statusMap[statusId]
  if (!statusKey) return ''

  return t(statusKey)
}

const formatTimelineDate = (date: string) => {
  if (!date) return ''

  const ticketDate = new Date(date)
  const today = new Date()

  // Sadece tarih kısmını karşılaştırmak için saat bilgisini sıfırla
  const ticketDateOnly = new Date(
    ticketDate.getFullYear(),
    ticketDate.getMonth(),
    ticketDate.getDate(),
  )
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  // Saat formatı (sadece saat:dakika)
  const hourMinutes = formatDate({ date, format: 'hourMinutes', persistent: true })

  // Bugün açılmış talepler için "Bugün, saat" formatı
  if (ticketDateOnly.getTime() === todayOnly.getTime()) {
    return `${t('chat.date.today')}, ${hourMinutes}`
  }

  // Diğer tarihler için "tarih, saat" formatı
  const dateStr = formatDate({ date, format: 'date', persistent: true })
  return `${dateStr}, ${hourMinutes}`
}

const timelineItems = computed(() => {
  if (!ticket.value) return []

  const items: Array<{ date: string; description: string }> = []

  items.push({
    date: formatTimelineDate(ticket.value.created_at),
    description: t('supportDetail.timeline.created'),
  })

  const statusHistory = ticket.value.status_history

  if (statusHistory && Array.isArray(statusHistory) && statusHistory.length > 0) {
    const sortedHistory = [...statusHistory].sort((a, b) => {
      const dateA = new Date(a.changed_at).getTime()
      const dateB = new Date(b.changed_at).getTime()
      return dateA - dateB
    })

    sortedHistory.forEach((historyItem) => {
      if (historyItem?.to_status?.id && historyItem?.changed_at) {
        items.push({
          date: formatTimelineDate(historyItem.changed_at),
          description: formatStatusDescription(historyItem.to_status.id),
        })
      }
    })
  }

  return items
})

const loadTicketData = async (ticketId: number) => {
  isLoading.value = true
  ticketStore.clearCurrentTicket()

  await ticketStore.getTicket(ticketId)

  const feedback = await feedbackStore.getFeedback(ticketId)
  feedback && (isShowFeedbackForm.value = false) // TODO: Uncomment this when feedback is implemented

  isLoading.value = false
}

onMounted(() => {
  loadTicketData(Number(route.query.id))
})

// Aynı overlay açıkken farklı ticket'a geçildiğinde
watch(
  () => route.query.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      loadTicketData(Number(newId))
    }
  },
)
</script>
